import { useCallback, useEffect, useState } from "react";
import SpeakButton from "@/components/SpeakButton";
import Celebration from "@/components/Celebration";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { ArrowLeft, Check, Lock } from "lucide-react";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import { useT } from "@/i18n";
import axios from "axios";
import { API } from "@/lib/api";
import BusinessBuilder from "@/components/business/BusinessBuilder";
import SellHonestly from "@/components/business/SellHonestly";
import WebWorkshop from "@/components/webdev/WebWorkshop";
import PhotoWorkshop from "@/components/photo/PhotoWorkshop";
import GamesWorkshop from "@/components/games/GamesWorkshop";
import AppBuilder from "@/components/appbuilder/AppBuilder";
import LanguagesWorkshop from "@/components/languages/LanguagesWorkshop";
import AiLab from "@/components/ailab/AiLab";
import StoryStudio from "@/components/story/StoryStudio";
const colorBg = {
  yellow: "bg-yellow-200",
  sky: "bg-sky-200",
  pink: "bg-pink-200",
  green: "bg-green-200",
  purple: "bg-purple-200",
  indigo: "bg-indigo-200",
  orange: "bg-orange-200",
  teal: "bg-teal-200",
};

export default function ModulePage() {
  const { moduleId } = useParams();
  const { lang, t } = useT();
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [module, setModule] = useState(null);
  const [progress, setProgress] = useState({});
  const [activeIdx, setActiveIdx] = useState(0);
  const [celebrate, setCelebrate] = useState(null);
  const closeCelebrate = useCallback(() => setCelebrate(null), []);

  useEffect(() => {
    axios.get(`${API}/modules/${moduleId}`).then((r) => setModule(r.data));
  }, [moduleId]);

  useEffect(() => {
    if (!isSignedIn) return;
    const api = makeApi(getToken);
    api.get("/me").then((r) => {
      const map = {};
      for (const p of r.data.progress || []) {
        if (p.module_id === moduleId) map[p.lesson_id] = true;
      }
      setProgress(map);
    });
  }, [getToken, isSignedIn, moduleId]);

  if (!module) return <div className="p-10">Loading...</div>;
  const lesson = module.lessons[activeIdx];
  const bg = colorBg[module.color] || "bg-sky-200";

  const markDone = async () => {
    if (!isSignedIn) {
      toast.error(lang === "es" ? "Entra primero para guardar progreso" : "Sign in first to save progress");
      return;
    }
    const api = makeApi(getToken);
    try {
      const r = await api.post("/progress", { module_id: moduleId, lesson_id: lesson.id, completed: true });
      setProgress((p) => ({ ...p, [lesson.id]: true }));
      setCelebrate({ badge: r.data.world_complete ? { module_id: moduleId, title: module.title, done: r.data.done, total: r.data.total } : null });
    } catch {
      toast.error(lang === "es" ? "No se pudo guardar, intenta otra vez" : "Couldn't save, try again");
    }
  };
  const lessonText = `${lesson.title?.[lang] || lesson.title?.es}. ${lesson.content?.[lang] || lesson.content?.es}`;

  return (
    <div className="min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 font-bold text-slate-700 hover:text-slate-900" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> {t.nav.dashboard}
        </Link>
        <h1 className="font-fredoka text-4xl sm:text-5xl font-bold mt-4 text-slate-900">
          {module.title?.[lang] || module.title?.es}
        </h1>
        <p className="text-slate-700 mt-2">{module.subtitle?.[lang]}</p>

        <div className="grid lg:grid-cols-3 gap-6 mt-10">
          {/* Lesson list */}
          <aside className="lg:col-span-1">
            <ul className="space-y-3">
              {module.lessons.map((l, idx) => {
                const done = progress[l.id];
                const active = idx === activeIdx;
                return (
                  <li key={l.id}>
                    <button
                      onClick={() => setActiveIdx(idx)}
                      className={`w-full text-left press-btn border-2 border-slate-900 rounded-2xl p-4 flex items-center gap-3 ${active ? bg + " chunky-shadow-sm" : "bg-white"}`}
                      data-testid={`lesson-tab-${l.id}`}
                    >
                      <span
                        className={`w-8 h-8 rounded-full border-2 border-slate-900 flex items-center justify-center text-sm font-bold ${done ? "bg-green-400" : "bg-white"}`}
                      >
                        {done ? <Check className="w-4 h-4" /> : idx + 1}
                      </span>
                      <span className="font-bold text-slate-900">
                        {l.title?.[lang] || l.title?.es}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </aside>

          {/* Lesson content */}
          <div className={`lg:col-span-2 ${bg} border-4 border-slate-900 chunky-shadow rounded-3xl p-7`}>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-700">
              {lang === "es" ? "Lección" : "Lesson"} {activeIdx + 1}
            </p>
            <h2 className="font-fredoka text-3xl font-bold mt-1 text-slate-900">
              {lesson.title?.[lang] || lesson.title?.es}
            </h2>
            <p className="mt-6 text-slate-800 text-lg leading-relaxed">
              {lesson.content?.[lang] || lesson.content?.es}
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="lesson-voice-buttons">
              <SpeakButton key={`l-${lesson.id}-${lang}`} mascot="lumi" text={lessonText} testId="lesson-speak-lumi" />
              <SpeakButton key={`s-${lesson.id}-${lang}`} mascot="spark" text={lessonText} testId="lesson-speak-spark" />
            </div>
            <div className="mt-8 flex items-center gap-3">
              {progress[lesson.id] ? (
                <div className="press-btn inline-flex items-center gap-2 bg-green-400 border-2 border-slate-900 rounded-full px-5 py-2 font-bold" data-testid="lesson-done-badge">
                  <Check className="w-5 h-5" /> {t.lesson.done}
                </div>
              ) : (
                <button
                  onClick={markDone}
                  disabled={!isLoaded}
                  className="press-btn inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-6 py-2.5 font-bold border-2 border-slate-900"
                  data-testid="mark-done-btn"
                >
                  <Check className="w-5 h-5" /> {t.lesson.markDone}
                </button>
              )}
              {!isSignedIn && (
                <span className="inline-flex items-center gap-1 text-sm text-slate-700">
                  <Lock className="w-4 h-4" />{" "}
                  <Link to="/sign-in" className="underline">
                    {t.nav.login}
                  </Link>
                </span>
              )}
            </div>
          </div>
        </div>
        {moduleId === "business" && <><BusinessBuilder /><SellHonestly /></>}
        {moduleId === "webdev" && <WebWorkshop />}
        {moduleId === "photography" && <PhotoWorkshop />}
        {moduleId === "games" && <GamesWorkshop />}
        {moduleId === "apps" && <AppBuilder />}
        {moduleId === "languages" && <LanguagesWorkshop />}
        {moduleId === "ai" && <AiLab />}
        {moduleId === "story" && <StoryStudio />}
      </div>
      <Celebration open={!!celebrate} badge={celebrate?.badge} onClose={closeCelebrate} />
    </div>
  );
}
